package com.trevorwiebe.timesheet.admin.reporting.domain

import com.trevorwiebe.timesheet.mileage.domain.US_STATES
import com.trevorwiebe.timesheet.mileage.domain.model.MileageEntry
import com.trevorwiebe.timesheet.mileage.domain.model.TripType
import com.trevorwiebe.timesheet.mileage.domain.model.TripTypeData

/**
 * One aggregated IFTA row: total miles driven and gallons burned in a single state.
 */
data class IftaStateTotal(
    val stateCode: String,
    val stateName: String,
    val miles: Long,
    val gallons: Double,
)

/**
 * Driving before a truck's first state-line crossing is assumed to be Kansas, where the
 * large majority of driving is done.
 */
private const val DEFAULT_STATE_CODE = "KS"

/**
 * Aggregates raw mileage entries into per-state IFTA totals.
 *
 * Entries are grouped by truck ([MileageEntry.equipmentId]) and walked in odometer order
 * (date breaks ties between identical readings) — the truck physically travels in odometer
 * order, so this makes the walk immune to entry-timestamp quirks (e.g. two manual entries
 * saved at the same minute) and keeps totals consistent with the General Mileage report's
 * max−min form. A running `currentState` (seeded to Kansas) tracks which state the truck
 * is in:
 *
 *  - Miles between two consecutive entries are attributed to the state the truck is in
 *    *before* the later entry, then the state switches when a STATE_LINE crossing is hit
 *    (odometer delta between crossings).
 *  - Fuel gallons are attributed to the current state, i.e. the state established by the
 *    most recent crossing (fuel between this state line and the next).
 *
 * Totals are summed across all trucks and returned as one row per state that has any
 * miles or gallons, sorted alphabetically by state name.
 */
fun calculateIftaReport(entries: List<MileageEntry>): List<IftaStateTotal> {
    val milesByState = mutableMapOf<String, Long>()
    val gallonsByState = mutableMapOf<String, Double>()

    entries.groupBy { it.equipmentId }.forEach { (_, truckEntries) ->
        val sorted = truckEntries.sortedWith(compareBy({ it.mileageValue }, { it.date }))
        var currentState = DEFAULT_STATE_CODE
        var prevOdometer: Long? = null

        for (entry in sorted) {
            prevOdometer?.let { prev ->
                val delta = entry.mileageValue - prev
                if (delta > 0) {
                    milesByState[currentState] = (milesByState[currentState] ?: 0L) + delta
                }
            }

            when (val data = entry.tripTypeData) {
                is TripTypeData.StateLine ->
                    if (entry.tripType == TripType.STATE_LINE) currentState = data.toState
                is TripTypeData.Fuel ->
                    gallonsByState[currentState] = (gallonsByState[currentState] ?: 0.0) + data.gallons
                else -> Unit
            }

            prevOdometer = entry.mileageValue
        }
    }

    val codes = milesByState.keys + gallonsByState.keys
    return codes.map { code ->
        IftaStateTotal(
            stateCode = code,
            stateName = US_STATES.firstOrNull { it.code == code }?.name ?: code,
            miles = milesByState[code] ?: 0L,
            gallons = gallonsByState[code] ?: 0.0,
        )
    }
        .filter { it.miles != 0L || it.gallons != 0.0 }
        .sortedBy { it.stateName }
}
